import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, X, GraduationCap, ShieldCheck } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const card = "bg-white dark:bg-neutral-900 border border-brand-line dark:border-neutral-800 rounded-2xl";
const inputCls = "mt-2 w-full rounded-xl border border-brand-line dark:border-neutral-700 bg-transparent px-4 py-2.5 focus:ring-2 focus:ring-brand-red focus:outline-none";
const empty = { name: "", email: "", phone: "", bio: "", certifications: [], background_check: "pending", active: true, payout_total: 0 };

export default function Instructors() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [certs, setCerts] = useState("");
  const load = () => api.get("/admin/instructors").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k, num) => (e) => setForm({ ...form, [k]: num ? Number(e.target.value) : e.target.value });

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/instructors", { ...form, certifications: certs.split(",").map((c) => c.trim()).filter(Boolean) });
      toast.success("Instructor added"); setOpen(false); setForm(empty); setCerts(""); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const setCheck = async (i, status) => { await api.put(`/admin/instructors/${i.id}`, { ...i, background_check: status }); toast.success("Updated"); load(); };
  const del = async (id) => { await api.delete(`/admin/instructors/${id}`); toast.success("Removed"); load(); };
  const badge = (s) => ({ cleared: "bg-green-100 text-green-700", pending: "bg-amber-100 text-amber-700", failed: "bg-red-100 text-red-700" }[s] || "bg-neutral-200");

  return (
    <div data-testid="instructors-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="font-display text-4xl">Instructors</h1><p className="text-brand-ink/60 dark:text-white/50 mt-2">Certifications, background checks, payouts.</p></div>
        <button onClick={() => setOpen(true)} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-red text-white font-medium hover:bg-brand-reddark transition-colors" data-testid="add-instructor-btn"><Plus className="w-4 h-4" /> Add instructor</button>
      </div>
      <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {items.length === 0 && <p className="text-brand-ink/50 dark:text-white/40" data-testid="instructors-empty">No instructors yet.</p>}
        {items.map((i) => (
          <div key={i.id} className={`${card} p-6`} data-testid={`instructor-${i.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2 font-medium"><GraduationCap className="w-5 h-5 text-brand-red" /> {i.name}</div>
              <button onClick={() => del(i.id)} className="text-brand-ink/30 hover:text-brand-red"><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="text-sm text-brand-ink/50 dark:text-white/40 mt-1">{i.email}</div>
            <p className="text-sm mt-3 text-brand-ink/70 dark:text-white/60">{i.bio}</p>
            <div className="mt-3 flex flex-wrap gap-1">{(i.certifications || []).map((c, x) => <span key={x} className="text-xs bg-brand-mist dark:bg-neutral-800 px-2 py-1 rounded-full">{c}</span>)}</div>
            <div className="mt-4 flex items-center justify-between">
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${badge(i.background_check)}`} data-testid={`bg-${i.id}`}><ShieldCheck className="w-3 h-3 inline mr-1" />{i.background_check}</span>
              <span className="text-sm">Payout: <strong>${(i.payout_total || 0).toFixed(2)}</strong></span>
            </div>
            <div className="mt-3 flex gap-2 text-xs">
              {["pending", "cleared", "failed"].map((s) => <button key={s} onClick={() => setCheck(i, s)} className="px-2 py-1 rounded border border-brand-line dark:border-neutral-700 hover:border-brand-red capitalize" data-testid={`set-${s}-${i.id}`}>{s}</button>)}
            </div>
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-neutral-900 rounded-2xl w-full max-w-lg p-8">
            <div className="flex items-center justify-between mb-6"><h2 className="font-display text-2xl">Add instructor</h2><button onClick={() => setOpen(false)}><X className="w-5 h-5" /></button></div>
            <form onSubmit={save} className="space-y-4">
              <input placeholder="Name" value={form.name} onChange={set("name")} required className={inputCls} data-testid="ins-name" />
              <input placeholder="Email" value={form.email} onChange={set("email")} className={inputCls} data-testid="ins-email" />
              <input placeholder="Certifications (comma separated)" value={certs} onChange={(e) => setCerts(e.target.value)} className={inputCls} data-testid="ins-certs" />
              <textarea placeholder="Bio" value={form.bio} onChange={set("bio")} rows={2} className={inputCls} data-testid="ins-bio" />
              <button type="submit" className="w-full px-6 py-3 rounded-full bg-brand-red text-white font-medium hover:bg-brand-reddark transition-colors" data-testid="ins-save">Add instructor</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
